import type { ComposicaoProposta } from './planos-cnpj.js';
import { separarLinhasPorFamilia } from './familias-produto.js';
import { criarConversorProposta } from './converter-proposta.js';
import { conferirFamiliaCpf, chaveLinha, type MetaFamiliaCpf } from './familia-cpf.js';
import { renovSemProdutoNovo } from './renov-produto-novo.js';
import { blocoRenovSemRenovacao } from './bloco-renov.js';
export interface CriarPedidoParams {
  clienteId: string;
  consultorId: string;
  origemLead?: string;
  composicao: ComposicaoProposta;
  gestorId?: string | null;
  pendingTabulacaoId?: string | null;
  leadId?: string | null;
}

export interface PedidoCriado {
  id: string;
  numero_pedido: number;
  familia: 'FIXA' | 'MOVEL';
  itensCreados: number;
}

export interface CriarPedidosResult {
  success: boolean;
  pedidos: PedidoCriado[];
  totalItens: number;
  errors: string[];
  precisouDividir: boolean;
}


export function criarConversorPorFamilia(supabase: any, conversor: ReturnType<typeof criarConversorProposta>) {
const { converterPropostaEmItens, invalidarCacheConversor } = conversor;
/**
 * Cria um único pedido no banco de dados
 */
async function criarPedidoBase(
  clienteId: string,
  consultorId: string,
  origemLead?: string,
  pendingTabulacaoId?: string | null,
  leadId?: string | null,
): Promise<{ id: string; numero_pedido: number } | null> {
  const { data: pedidoCriado, error } = await supabase
    .from('pedidos')
    .insert([{
      cliente_id: clienteId,
      consultor_id: consultorId,
      data_pedido: new Date().toISOString().split('T')[0],
      data_referencia: new Date().toISOString().split('T')[0],
      numero_pedido: 0, // Gerado automaticamente pelo trigger
      origem_lead: origemLead || 'MANUAL',
      lead_origem_id: leadId || null,
    }])
    .select('id, numero_pedido')
    .maybeSingle();
  let pedido = pedidoCriado;

  // ROBUSTEZ (15/06): o INSERT cria o pedido no banco, mas em alguns casos a resposta
  // volta sem corpo/id (intermitente no client). Sem o id, o item não anexa => o usuário
  // via "Nenhum pedido foi criado" e ficava pedido órfão. Fallback: re-busca o pedido
  // recém-criado (mais recente desse cliente+consultor) e segue normalmente.
  if (!error && !pedido?.id) {
    const { data: recente } = await supabase
      .from('pedidos')
      .select('id, numero_pedido')
      .eq('cliente_id', clienteId)
      .eq('consultor_id', consultorId)
      .gte('created_at', new Date(Date.now() - 20000).toISOString())
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (recente?.id) {
      pedido = recente;
      console.warn('⚠️ [CRIAR_PEDIDO] id ausente no insert — recuperado via re-busca:', pedido.id);
    }
  }

  if (error) {
    console.error('❌ [CRIAR_PEDIDO] Erro ao criar pedido:', error);
    throw error;
  }

  if (!pedido?.id) {
    console.error('❌ [CRIAR_PEDIDO] Pedido sem confirmação do CRM');
    throw new Error('O CRM não confirmou a criação do pedido. Atualize a lista antes de tentar novamente.');
  }

  console.log('✅ [CRIAR_PEDIDO] Pedido criado:', pedido.id, 'Número:', pedido.numero_pedido);

  // Vincular tabulação se existir (apenas para o primeiro pedido)
  if (pendingTabulacaoId) {
    console.log('📝 [CRIAR_PEDIDO] Vinculando tabulação:', pendingTabulacaoId);
    const { error: errorTab } = await supabase
      .from('discadora_historico_tabulacao')
      .update({ pedido_id: pedido.id })
      .eq('id', pendingTabulacaoId);

    if (errorTab) {
      console.error('❌ [CRIAR_PEDIDO] Erro ao vincular tabulação:', errorTab);
    }
  }

  return pedido;
}

/**
 * Cria pedidos separados por família de produtos
 *
 * Se a proposta tem FIXA + MÓVEL → 2 pedidos
 * Se só tem uma família → 1 pedido
 */
async function criarPedidosPorFamilia({
  clienteId,
  consultorId,
  origemLead,
  composicao,
  gestorId,
  pendingTabulacaoId,
  leadId,
}: CriarPedidoParams): Promise<CriarPedidosResult> {
  const errors: string[] = [];
  const pedidosCriados: PedidoCriado[] = [];
  let totalItens = 0;

  if (!composicao.linhas || composicao.linhas.length === 0) {
    return {
      success: false,
      pedidos: [],
      totalItens: 0,
      errors: ['Proposta sem linhas'],
      precisouDividir: false,
    };
  }

  const semRenovacao = blocoRenovSemRenovacao(composicao.linhas);
  if (semRenovacao) {
    return { success: false, pedidos: [], totalItens: 0, errors: [semRenovacao], precisouDividir: false };
  }

  if (composicao.tipoPessoa === 'PF' || composicao.linhas.some(linha => linha.plano?.tipoPessoa === 'PF')) {
    const linhas = [];
    const metaPorLinha = new Map<string, MetaFamiliaCpf>();
    for (const linha of composicao.linhas) {
      if (!linha.plano) { errors.push('Carregue os planos CPF da proposta antes de criar o pedido.'); continue; }
      const codigo = linha.planoId || linha.plano?.id;
      const { data: plano, error } = await supabase.from('planos_proposta_catalogo')
        .select('produto_pedido_id,metadata').eq('codigo', codigo).eq('tipo_pessoa', 'PF')
        .order('ativo', { ascending: false }).limit(1).maybeSingle();
      if (error) throw error;
      if (plano?.metadata?.escopo === 'referencia_comercial_pf') {
        errors.push(`A oferta ${codigo} é referência comercial: confirme a composição formal, os dois períodos de preço e os produtos CPF antes de gerar pedido.`);
        continue;
      }
      if (!plano?.produto_pedido_id) {
        errors.push(`O plano CPF ${linha.plano?.nome || codigo || linha.tipo} precisa de vínculo com um produto CPF em Planos & Preços antes de criar o pedido.`);
        continue;
      }
      const { data: produto, error: erroProduto } = await supabase.from('produtos')
        .select('id, tipo_pessoa, ativo').eq('id', plano.produto_pedido_id).single();
      if (erroProduto) throw erroProduto;
      if (produto?.tipo_pessoa !== 'PF' || !produto.ativo) {
        errors.push(`O produto vinculado a ${codigo} precisa estar ativo e marcado como CPF.`);
        continue;
      }
      metaPorLinha.set(chaveLinha(linha), (plano.metadata ?? {}) as MetaFamiliaCpf);
      linhas.push({ ...linha, plano: { ...linha.plano, produtoPedidoId: produto.id } });
    }
    // ⛔ Mesmo plano duas vezes no CPF não passa, e dependente só existe ligado a um
    // Pós, na ordem incluído → pago (Douglas 29/09/2026). Lido do catálogo, não da tela.
    if (!errors.length) errors.push(...conferirFamiliaCpf(linhas, (l) => metaPorLinha.get(chaveLinha(l))).erros);
    // ⛔ RENOV CPF sem produto novo não vira pedido (Douglas 06/10/2026): a mesma régua
    // da Conecta (renovSemProdutoNovo), para a IA e para a tela.
    // A leitura do lead nunca derruba o pedido: em 06/10 ela não estava liberada no
    // conversor e todo pedido CPF falhou por 1 h ("Tabela fora do conversor").
    if (!errors.length && leadId) {
      let lead: any = null;
      try { lead = (await supabase.from('leads_carteira').select('tipo_lead').eq('id', leadId).maybeSingle())?.data; } catch { lead = null; }
      const semNovo = renovSemProdutoNovo((lead as any)?.tipo_lead,
        linhas.map((l: any) => ({ tipo: l.tipo, plano_codigo: l.planoId || l.plano?.id })));
      if (semNovo) errors.push(semNovo);
    }
    if (errors.length) return { success: false, pedidos: [], totalItens: 0, errors, precisouDividir: false };
    composicao = { ...composicao, linhas };
  }

  // Separar linhas por família
  const { fixaBasica, movel } = separarLinhasPorFamilia(composicao.linhas);
  const precisouDividir = fixaBasica.length > 0 && movel.length > 0;

  console.log('🏠 [FAMILIAS] Criando pedidos:', {
    precisouDividir,
    linhasFixa: fixaBasica.length,
    linhasMovel: movel.length,
  });

  // 1. Criar pedido MÓVEL se tiver linhas móveis
  if (movel.length > 0) {
    console.log('📱 [FAMILIAS] Criando pedido MÓVEL...');

    const pedidoMovel = await criarPedidoBase(
      clienteId,
      consultorId,
      origemLead,
      pendingTabulacaoId, // Tabulação vai para o primeiro pedido
      leadId,
    );

    if (!pedidoMovel) {
      errors.push('Erro ao criar pedido MÓVEL');
    } else {
      // Converter linhas móveis em itens
      const composicaoMovel: ComposicaoProposta = {
        ...composicao,
        linhas: movel,
      };

      invalidarCacheConversor(); // Garantir mapeamentos frescos
      let resultado = await converterPropostaEmItens({
        pedidoId: pedidoMovel.id,
        composicao: composicaoMovel,
        gestorId,
      });
      // RETRY: se gerou 0 itens (falha transiente no front/gateway durante a conversao),
      // tenta UMA vez mais com cache fresco. So quando 0 (nada criado) -> sem risco de
      // duplicar item. Era a causa de pedido nascer VAZIO (fantasma). (fix 25/06)
      if (resultado.itensCreados === 0 && composicaoMovel.linhas.length > 0) {
        console.warn('🔁 [FAMILIAS] MÓVEL gerou 0 itens — retry da conversão');
        invalidarCacheConversor();
        resultado = await converterPropostaEmItens({ pedidoId: pedidoMovel.id, composicao: composicaoMovel, gestorId });
      }

      pedidosCriados.push({
        id: pedidoMovel.id,
        numero_pedido: pedidoMovel.numero_pedido,
        familia: 'MOVEL',
        itensCreados: resultado.itensCreados,
      });

      totalItens += resultado.itensCreados;

      if (!resultado.success) {
        errors.push(...resultado.errors.map(e => `[MÓVEL] ${e}`));
      }

      // Vincular lead ao pedido móvel (principal)
      if (leadId) {
        await supabase
          .from('pedidos')
          .update({ lead_origem_id: leadId })
          .eq('id', pedidoMovel.id);
      }
    }
  }

  // 2. Criar pedido FIXA se tiver linhas fixa
  // ⛔ FALHA NA FIXA NAO PODE DERRUBAR O MOVEL (Douglas 02/09/2026).
  // `criarPedidoBase` passou a fazer `throw`, e este bloco roda DEPOIS de o
  // pedido movel ja existir com todos os itens. O throw escapava da funcao
  // inteira: a blindagem anti-fantasma nao rodava, o lead nao era ligado ao
  // pedido (`pedido_id` ficava null) e, na tentativa seguinte, a trava de
  // idempotencia liberava — nascia um SEGUNDO pedido movel completo. Agora a
  // falha da fixa vira erro na lista; o retorno ja sabe conviver com sucesso
  // parcial e quem chama transforma `totalItens > 0` em pedido valido.
  if (fixaBasica.length > 0) {
    try {
    console.log('🏠 [FAMILIAS] Criando pedido FIXA...');

    const pedidoFixa = await criarPedidoBase(
      clienteId,
      consultorId,
      origemLead,
      movel.length === 0 ? pendingTabulacaoId : null, // Só vincula tabulação se não tiver móvel
      leadId,
    );

    if (!pedidoFixa) {
      errors.push('Erro ao criar pedido FIXA');
    } else {
      // Converter linhas fixa em itens
      const composicaoFixa: ComposicaoProposta = {
        ...composicao,
        linhas: fixaBasica,
        // Remover flag de combo para cálculos corretos
        incluiFibra: composicao.incluiFibra && movel.length === 0,
      };

      let resultado = await converterPropostaEmItens({
        pedidoId: pedidoFixa.id,
        composicao: composicaoFixa,
        gestorId,
      });
      // RETRY (mesma logica do MÓVEL): 0 itens com proposta -> tenta de novo. (fix 25/06)
      if (resultado.itensCreados === 0 && composicaoFixa.linhas.length > 0) {
        console.warn('🔁 [FAMILIAS] FIXA gerou 0 itens — retry da conversão');
        invalidarCacheConversor();
        resultado = await converterPropostaEmItens({ pedidoId: pedidoFixa.id, composicao: composicaoFixa, gestorId });
      }

      pedidosCriados.push({
        id: pedidoFixa.id,
        numero_pedido: pedidoFixa.numero_pedido,
        familia: 'FIXA',
        itensCreados: resultado.itensCreados,
      });

      totalItens += resultado.itensCreados;

      if (!resultado.success) {
        errors.push(...resultado.errors.map(e => `[FIXA] ${e}`));
      }

      // Vincular lead ao pedido fixa SEMPRE (independente de ter móvel ou não)
      if (leadId) {
        await supabase
          .from('pedidos')
          .update({ lead_origem_id: leadId })
          .eq('id', pedidoFixa.id);
      }
    }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.error('❌ [FAMILIAS] Falha no pedido FIXA (o MÓVEL segue de pé):', msg);
      errors.push(`[FIXA] ${msg}`);
    }
  }

  // 🛡️ BLINDAGEM ANTI-FANTASMA: se criou pedido(s) mas a conversao gerou 0 itens (mesmo
  // apos o retry), REMOVE o(s) pedido(s) vazio(s) — nunca deixa pedido fantasma sem item.
  // Nesse caminho o AbrirPedidoModal aborta e NAO liga o lead (lead.pedido_id fica null),
  // entao e' seguro soft-deletar o pedido. O consultor simplesmente re-abre. (fix raiz 25/06)
  if (pedidosCriados.length > 0 && totalItens === 0) {
    const idsVazios = pedidosCriados.map(p => p.id);
    console.warn('🗑️ [FAMILIAS] Conversao gerou 0 itens — removendo pedido(s) vazio(s):', idsVazios);
    try {
      await supabase.from('pedidos').update({ deletado: true }).in('id', idsVazios);
    } catch (e) {
      console.error('[FAMILIAS] Falha ao remover pedido vazio (segue recuperavel):', e);
    }
    return {
      success: false,
      pedidos: [],
      totalItens: 0,
      errors: errors.length > 0 ? errors : ['A proposta nao gerou itens (pedido vazio removido)'],
      precisouDividir,
    };
  }

  const success = pedidosCriados.length > 0 && (totalItens > 0 || errors.length === 0);

  if (precisouDividir) {
    console.log('✅ [FAMILIAS] Proposta dividida em 2 pedidos:', {
      pedidoMovel: pedidosCriados.find(p => p.familia === 'MOVEL')?.numero_pedido,
      pedidoFixa: pedidosCriados.find(p => p.familia === 'FIXA')?.numero_pedido,
      totalItens,
    });
  }

  return {
    success,
    pedidos: pedidosCriados,
    totalItens,
    errors,
    precisouDividir,
  };
}


return { criarPedidosPorFamilia };
}
